import { lonLatToMercator } from "./geo";
import { EFFIS_GWIS_WMS } from "./sources";

/** EFFIS Europe FWI danger classes (JRC thresholds). */
export const FWI_CLASSES = [
  { max: 5.2, id: "vl", label: "Very low", color: "#4dac26" },
  { max: 11.2, id: "l", label: "Low", color: "#a6d96a" },
  { max: 21.3, id: "m", label: "Moderate", color: "#ffe066" },
  { max: 38, id: "h", label: "High", color: "#fdae61" },
  { max: 50, id: "vh", label: "Very high", color: "#f46d43" },
  { max: Infinity, id: "x", label: "Extreme", color: "#d73027" },
] as const;

export type FwiSample = {
  fwi: number;
  isi: number | null;
  bui: number | null;
  ffmc: number | null;
  dmc: number | null;
  dc: number | null;
  anomaly: number | null;
  ranking: number | null;
};

export function fwiClass(value: number): (typeof FWI_CLASSES)[number] {
  return FWI_CLASSES.find((c) => value < c.max) ?? FWI_CLASSES[FWI_CLASSES.length - 1]!;
}

/** EFFIS writes -9999 on water / nodata. That is not "very high". */
export function saneFwi(value: number | null | undefined): number | null {
  if (value == null || value < 0) return null;
  return value;
}

/** Browser → EFFIS directly (CORS open). ~8 km box, pixel 128,128 is the click. */
export function effisFwiInfoUrl(lon: number, lat: number, timeIsoDate: string): string {
  const [x, y] = lonLatToMercator(lon, lat);
  const pad = 4000;
  const q = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.1.1",
    REQUEST: "GetFeatureInfo",
    LAYERS: "ecmwf.query",
    QUERY_LAYERS: "ecmwf.query",
    STYLES: "default",
    FORMAT: "image/png",
    INFO_FORMAT: "text/html",
    SRS: "EPSG:3857",
    WIDTH: "256",
    HEIGHT: "256",
    X: "128",
    Y: "128",
    BBOX: `${x - pad},${y - pad},${x + pad},${y + pad}`,
    TIME: timeIsoDate,
  });
  return `${EFFIS_GWIS_WMS}?${q.toString()}`;
}

export function parseEffisFwiHtml(html: string): FwiSample | null {
  const num = (label: string): number | null => {
    const m = html.match(new RegExp(`${label}[^<]*</td>\\s*<td>(-?[0-9.]+)`, "i"));
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  };
  const fwi = num("Fire Weather Index");
  if (fwi == null) return null;
  return {
    fwi,
    isi: num("Initial Spread Index"),
    bui: num("Build Up Index"),
    ffmc: num("Fine Fuel Moisture Code"),
    dmc: num("Duff Moisture Code"),
    dc: num("Drought Code"),
    anomaly: num("Anomaly Index"),
    ranking: num("Ranking Index"),
  };
}
